/**
 * Events module — trackEvent + batching.
 *
 * Behavior:
 *   * trackEvent() is synchronous and never throws (fire-and-forget).
 *   * Events buffer in memory until one of these flush triggers fires:
 *       - 5-second interval (set up on construction)
 *       - buffer reaches 20 events
 *       - page is about to unload (beforeunload)
 *   * On beforeunload, the send uses `keepalive: true` so the request
 *     survives the navigation.
 *   * A flush is sent in as many calls as the server's per-call limits
 *     need (see MAX_EVENTS_PER_CALL and MAX_BATCH_BYTES).
 *   * Errors during flush are logged (debug) and the rest of the flush is
 *     dropped; re-queueing on failure could cause infinite loops when the
 *     backend is persistently unavailable.
 */
import type { EventProperties } from './types';
import type { SdkContext } from './internal/context';
import { invokeEdge } from './internal/edgeFunctions';

interface BufferedEvent {
  name: string;
  properties?: EventProperties;
  timestamp: string;
}

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 20;

// Per-event-name length cap. Mirrors the server-side check in the
// `track-events` Edge Function (200 chars). Client-side enforcement
// keeps a single malformed event from poisoning an entire batch —
// without it, one over-long name causes the server to reject the
// whole flush with 400 INVALID_BODY and the other 99 valid events
// get dropped along with it.
const NAME_MAX_LENGTH = 200;

// The other limits `track-events` enforces, for the same reason as the name
// cap: the server refuses a whole call over any one of them, so every event
// in that call is lost, not only the one at fault. Mirrors
// supabase/functions/_shared/eventBudget.ts and track-events/index.ts.
//
// Per event: properties must be a plain object under 9000 UTF-8 bytes.
const MAX_EVENT_PROPERTIES_BYTES = 9000;
// Per call: at most 100 events, and at most 256 KiB of event data counted as
// the server counts it (name bytes plus serialized-properties bytes).
const MAX_EVENTS_PER_CALL = 100;
const MAX_BATCH_BYTES = 262_144;

// Browsers refuse a keepalive request once the bodies of all keepalive
// requests in flight exceed 64 KiB. The unload flush is the only keepalive
// send, so it stays under this across all of its calls, leaving the rest of
// the quota for the page's own. Events past it cannot survive the unload
// either way; they are dropped rather than sent to be refused.
const KEEPALIVE_BODY_BUDGET = 60 * 1024;

const utf8 = new TextEncoder();
const utf8Length = (text: string) => utf8.encode(text).length;

/** A buffered event and what it costs, measured once at track() time. */
interface Queued {
  event: BufferedEvent;
  /** What the server charges: name bytes plus serialized-properties bytes. */
  cost: number;
  /** Its bytes on the wire, as one element of the request's events array. */
  wire: number;
}

export class EventsModule {
  private buffer: Queued[] = [];
  // Events a flush in progress has taken but not yet sent. A field, not a
  // local, so the unload flush can take them over: otherwise a page left
  // mid-flush loses every call not yet started.
  private pending: Queued[] = [];
  private inflight: Promise<void> | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private unloadHandler: (() => void) | null = null;
  private disposed = false;
  private envelopeSize: number | null = null;

  constructor(private readonly ctx: SdkContext) {
    this.flushTimer = setInterval(() => {
      void this.flush();
    }, FLUSH_INTERVAL_MS);

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this.unloadHandler = () => this.flushOnUnload();
      window.addEventListener('beforeunload', this.unloadHandler);
      // pagehide fires in more scenarios than beforeunload (mobile
      // background, bfcache). Listen to both for better coverage.
      window.addEventListener('pagehide', this.unloadHandler);
    }
  }

  /**
   * Synchronous, fire-and-forget. Silently drops bad input.
   */
  track(name: string, properties?: EventProperties): void {
    if (this.disposed) return;
    if (typeof name !== 'string' || !name) {
      this.ctx.logger.debug('trackEvent: ignored — name must be a non-empty string');
      return;
    }
    if (name.length > NAME_MAX_LENGTH) {
      // Mirror the server-side cap so a single long-name event can't
      // poison the entire batch on flush. Same "silently drop" contract
      // as the typeof check above — fire-and-forget logging via the
      // debug channel is the only feedback to the game integrator.
      this.ctx.logger.debug(
        `trackEvent: ignored — name length ${name.length} exceeds limit of ${NAME_MAX_LENGTH}`,
      );
      return;
    }
    // Snapshot via a JSON round-trip at track() time, for two reasons.
    // (1) An event should record state at the moment it happened, not
    // whatever the caller mutated the object into by flush time.
    // (2) The whole batch is re-serialized in one JSON.stringify at
    // flush — an object that became circular after track(), or a getter
    // that throws on a later read, would fail that call and drop every
    // event in the batch. A round-tripped snapshot is plain JSON data,
    // so the flush-time serialization cannot throw. Failures here drop
    // only this event — same "silently drop bad input" contract as the
    // name guards above.
    let snapshot: EventProperties | undefined;
    let propertiesJson: string | undefined;
    if (properties !== undefined) {
      try {
        propertiesJson = JSON.stringify(properties);
        snapshot = JSON.parse(propertiesJson) as EventProperties;
      } catch {
        this.ctx.logger.debug('trackEvent: ignored — properties not JSON-serializable');
        return;
      }
      // Checked on the snapshot, which is what gets sent: a Date or a class
      // instance with toJSON serializes to a string, and an array stays one.
      // The server takes only a plain object (or null) here.
      if (
        snapshot !== null &&
        (typeof snapshot !== 'object' || Array.isArray(snapshot))
      ) {
        this.ctx.logger.debug('trackEvent: ignored — properties must be a plain object');
        return;
      }
    }
    const propertyBytes = propertiesJson === undefined || snapshot === null
      ? 0
      : utf8Length(propertiesJson);
    if (propertyBytes >= MAX_EVENT_PROPERTIES_BYTES) {
      this.ctx.logger.debug(
        `trackEvent: ignored — properties are ${propertyBytes} bytes, limit ${MAX_EVENT_PROPERTIES_BYTES}`,
      );
      return;
    }
    const event: BufferedEvent = {
      name,
      properties: snapshot,
      timestamp: new Date().toISOString(),
    };
    this.buffer.push({
      event,
      cost: utf8Length(name) + propertyBytes,
      wire: utf8Length(JSON.stringify(event)),
    });
    if (this.buffer.length >= FLUSH_THRESHOLD) {
      void this.flush();
    }
  }

  /**
   * Explicit flush — awaits in-flight sends. Safe to call any time.
   *
   * A backlog over one call's limits goes out as consecutive calls, and this
   * resolves when the last has answered. A failure stops the flush at once,
   * so an unreachable backend costs one timeout, not one per call.
   */
  async flush(): Promise<void> {
    if (this.disposed) return;
    if (this.inflight) return this.inflight;
    if (this.buffer.length === 0) return;

    this.pending = this.buffer;
    this.buffer = [];

    this.inflight = this.sendAll().finally(() => {
      this.inflight = null;
    });

    return this.inflight;
  }

  /**
   * Stop timers + detach listeners. Called by Vibecade.dispose() if
   * callers need clean teardown (e.g., SPA navigation away from an
   * SDK-using view).
   */
  dispose(): void {
    this.disposed = true;
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.unloadHandler && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.unloadHandler);
      window.removeEventListener('pagehide', this.unloadHandler);
      this.unloadHandler = null;
    }
  }

  // -------------------------------------------------------------------------
  // private
  // -------------------------------------------------------------------------

  private flushOnUnload(): void {
    // A flush in progress hands over the calls it has not started; they are
    // older than the buffer, so they go first. The call already on the wire
    // is not resent: whether it landed is unknown, and a resend could count
    // its events twice.
    const queue = [...this.pending, ...this.buffer];
    this.pending = [];
    this.buffer = [];
    if (queue.length === 0) return;
    // Fire and forget with keepalive so the POSTs survive the unload. No
    // await — beforeunload doesn't wait for promises. All calls start at
    // once, so they share the keepalive quota at once: sized together.
    let budget = KEEPALIVE_BODY_BUDGET;
    while (queue.length > 0) {
      const chunk = this.takeChunk(queue, budget);
      if (chunk.length === 0) break;
      budget -= this.bodyBytes(chunk);
      this.send(chunk, { keepalive: true }).catch(() => {});
    }
    if (queue.length > 0) {
      this.ctx.logger.debug(
        `track-events: ${queue.length} events over the unload size limit were dropped`,
      );
    }
  }

  /**
   * Send a flush as consecutive calls, each within the server's limits. The
   * first failure drops the rest of the flush: the common failure is the
   * backend being unreachable, and each further call would only wait out its
   * own timeout to fail the same way.
   */
  private async sendAll(): Promise<void> {
    // Read from the field on every pass: the unload flush may have taken the
    // remainder while a call was awaited, and then there is nothing left here.
    while (this.pending.length > 0) {
      // dispose() promises the module goes quiet. A call already on the wire
      // finishes; the ones after it are not started.
      if (this.disposed) {
        this.ctx.logger.debug(
          `track-events: disposed mid-flush; dropped ${this.pending.length} events`,
        );
        this.pending = [];
        return;
      }
      const chunk = this.takeChunk(this.pending, Infinity);
      try {
        await this.send(chunk);
      } catch (e) {
        this.ctx.logger.debug(
          `track-events flush failed; dropped ${chunk.length + this.pending.length} events:`,
          e,
        );
        // Drop the rest. See class doc for rationale.
        this.pending = [];
        return;
      }
    }
  }

  /**
   * Remove and return the longest run from the front of `queue` that one
   * call may carry: at most MAX_EVENTS_PER_CALL events, at most
   * MAX_BATCH_BYTES by the server's count, and a request body no larger than
   * `bodyBudget`. Empty when not even the first event fits the budget.
   */
  private takeChunk(queue: Queued[], bodyBudget: number): Queued[] {
    let n = 0;
    let cost = 0;
    // The body is the envelope plus each event and the commas between them.
    let body = this.envelopeBytes() - 1;
    while (n < queue.length && n < MAX_EVENTS_PER_CALL) {
      const next = queue[n];
      if (cost + next.cost > MAX_BATCH_BYTES) break;
      if (body + next.wire + 1 > bodyBudget) break;
      cost += next.cost;
      body += next.wire + 1;
      n++;
    }
    return queue.splice(0, n);
  }

  /** The exact request body size of `chunk`: see takeChunk. */
  private bodyBytes(chunk: Queued[]): number {
    return chunk.reduce((sum, q) => sum + q.wire + 1, this.envelopeBytes() - 1);
  }

  /** The body with an empty events array. Constant: config never changes. */
  private envelopeBytes(): number {
    this.envelopeSize ??= utf8Length(JSON.stringify(this.body([])));
    return this.envelopeSize;
  }

  private body(events: BufferedEvent[]): Record<string, unknown> {
    return {
      gameId: this.ctx.config.gameId,
      apiKey: this.ctx.config.apiKey,
      events,
    };
  }

  private async send(
    chunk: Queued[],
    opts: { keepalive?: boolean } = {},
  ): Promise<void> {
    await invokeEdge<{ accepted: number }>(
      this.ctx,
      'track-events',
      this.body(chunk.map((q) => q.event)),
      opts,
    );
  }
}
